import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@lms/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';
import { useAuthStore } from '../store/auth';
import { ProfileDialog } from './ProfileDialog';

interface ChangeEmailModalProps {
  onClose: () => void;
}
const resendAvailableAt = new Map<string, number>();

export function ChangeEmailModal({ onClose }: ChangeEmailModalProps) {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const accountId = user?.id ?? '';
  const [cooldown, setCooldown] = useState(() =>
    Math.max(0, Math.ceil(((resendAvailableAt.get(accountId) ?? 0) - Date.now()) / 1000))
  );
  const request = useMutation({
    gcTime: 0,
    mutationFn: (data: Parameters<typeof apiClient.auth.requestEmailChange>[0]) =>
      apiClient.auth.requestEmailChange(data),
    onSuccess: () => {
      resendAvailableAt.set(accountId, Date.now() + 60_000);
      setAccepted(true);
      setCooldown(60);
      setError('');
    },
    onError: () => setError(t(copy.profile.emailRequestError)),
    onSettled: () => {
      setCurrentPassword('');
      setShowPassword(false);
      request.reset();
    },
  });
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (request.isPending || cooldown > 0) return;
    const value = email.trim().toLowerCase();
    const invalidEmail =
      value.length > 254 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
      value === user?.email?.toLowerCase();
    setEmailError(invalidEmail ? t(copy.profile.emailInvalid) : '');
    setPasswordError(!currentPassword ? t(copy.profile.passwordRequired) : '');
    if (invalidEmail || !currentPassword) return;
    setError('');
    request.mutate({ email: value, currentPassword });
  };
  return (
    <ProfileDialog
      title={t(copy.profile.changeEmail)}
      description={t(copy.profile.changeEmailDescription)}
      busy={request.isPending}
      onClose={onClose}
    >
      <form
        noValidate
        onSubmit={submit}
        className="flex min-h-0 flex-col"
        aria-busy={request.isPending}
      >
        <div className="space-y-6 overflow-y-auto px-6 pb-6">
          <div className="space-y-2 text-sm text-on-surface-variant">
            <p className="font-bold">{t(copy.profile.email)}</p>
            <p className="break-all">{user?.email}</p>
          </div>
          {accepted && (
            <p role="status" className="rounded-lg bg-primary/10 p-4 text-sm text-primary">
              {t(copy.profile.emailRequested)}
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-lg bg-error-container p-4 text-sm text-on-error-container"
            >
              {error}
            </p>
          )}
          <fieldset disabled={request.isPending} className="space-y-6">
            <div>
              <label
                htmlFor="change-email-address"
                className="mb-2 block text-sm font-bold text-on-surface"
              >
                {t(copy.profile.newEmail)}
              </label>
              <input
                id="change-email-address"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  setEmailError('');
                }}
                className="w-full rounded-lg bg-surface-container-high px-4 py-3 text-on-surface focus:outline-primary"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'change-email-address-error' : undefined}
              />
              {emailError && (
                <p id="change-email-address-error" role="alert" className="mt-2 text-sm text-error">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="change-email-password"
                className="mb-2 block text-sm font-bold text-on-surface"
              >
                {t(copy.profile.currentPasswordLabel)}
              </label>
              <div className="relative">
                <input
                  id="change-email-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={event => {
                    setCurrentPassword(event.target.value);
                    setPasswordError('');
                  }}
                  className="w-full rounded-lg bg-surface-container-high px-4 py-3 pe-14 text-on-surface focus:outline-primary"
                  aria-invalid={Boolean(passwordError)}
                  aria-describedby={passwordError ? 'change-email-password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(value => !value)}
                  aria-label={t(
                    showPassword ? copy.profile.hidePassword : copy.profile.showPassword
                  )}
                  aria-pressed={showPassword}
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-on-surface-variant focus-visible:outline-2 focus-visible:outline-primary"
                >
                  {showPassword ? (
                    <EyeOff size={20} aria-hidden="true" />
                  ) : (
                    <Eye size={20} aria-hidden="true" />
                  )}
                </button>
              </div>
              {passwordError && (
                <p
                  id="change-email-password-error"
                  role="alert"
                  className="mt-2 text-sm text-error"
                >
                  {passwordError}
                </p>
              )}
            </div>
          </fieldset>
          {cooldown > 0 && (
            <p className="text-sm text-on-surface-variant">
              {t(copy.profile.emailCooldown).replace('{seconds}', String(cooldown))}
            </p>
          )}
        </div>
        <footer className="flex shrink-0 flex-col-reverse gap-3 bg-surface-container-low px-6 py-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" disabled={request.isPending} onClick={onClose}>
            {t(copy.common.cancel)}
          </Button>
          <Button
            type="submit"
            disabled={request.isPending || cooldown > 0}
            className="bg-gradient-to-r from-primary to-primary-container"
          >
            {t(
              request.isPending
                ? copy.profile.saving
                : accepted
                  ? copy.profile.emailResend
                  : copy.profile.emailRequest
            )}
          </Button>
        </footer>
      </form>
    </ProfileDialog>
  );
}
